import * as THREE from 'three';
import { bevelBox, chamferCyl, chamferCone, stippleField } from './Bevel.js';
import { place } from './Builder.js';

/**
 * Tactical-gloved first-person arms.
 *
 * ## Contact
 *
 * Contact is *solved*, not eyeballed. A grip is described as a cylinder (axis, radius);
 * the palm is wrapped around it as a three-segment chain, and each finger is wrapped on
 * from where the palm ends. Every wrap step is a per-joint bisection that lands the next
 * joint exactly `radius + halfThickness` from the grip axis, so floating fingers and
 * fingers buried in the gun are both impossible by construction.
 *
 * The trigger finger is solved the same way, against the trigger blade instead of the
 * grip: `solveTriggerIK` runs a two-parameter planar solve in the weapon's bore plane
 * until the pad of the distal phalanx sits one half-thickness from the blade anchor. The
 * bind pose is authored at a fraction of that curl (finger indexed along the frame) and
 * the remainder is published as `meta.triggerCurl` for the runtime to blend in.
 * Previously the runtime curls were hand-typed constants AND the finger's curl axis came
 * from the grip cylinder, so it swung sideways across the receiver; the fingertip
 * finished 35-92 mm short on all four weapons.
 *
 * ## Geometry
 *
 * Each digit is ONE continuous swept surface across all three phalanges, skinned with
 * real weights that straddle each joint, and each hand is one continuous palm loft with
 * a thenar bulge, knuckle rise and cuff. The previous build stacked two bevelled boxes
 * plus a capsule per phalanx, which is what produced the "smooth grey capsule-stacks
 * with visible ring seams" the critique rejected.
 *
 * Bone convention: every hand/finger bone has +X along its own segment, +Y out of the
 * back of the hand, +Z toward the little finger (so the thumb is on -Z for a right
 * hand, +Z for a left).
 */

const FINGERS = [
  { name: 'index', lens: [0.0450, 0.0262, 0.0228], w: 0.0198, h: 0.0192 },
  { name: 'middle', lens: [0.0498, 0.0308, 0.0240], w: 0.0196, h: 0.0196 },
  { name: 'ring', lens: [0.0458, 0.0286, 0.0230], w: 0.0184, h: 0.0186 },
  { name: 'pinky', lens: [0.0372, 0.0212, 0.0202], w: 0.0164, h: 0.0166 },
];
const THUMB = { lens: [0.0230, 0.0330, 0.0262], w: 0.0224, h: 0.0212 };

const PALM_LEN = 0.0920;
const PALM_W = 0.0860;
const PALM_T = 0.0290;
const PALM_SEG = [0.30, 0.37, 0.33];

/** Index-finger curl coupling: proximal : middle : distal. */
const TRIGGER_COUPLE = [1.0, 1.15, 0.72];
/** How much of the solved trigger curl is baked into the bind (rest) pose. */
const TRIGGER_REST_FRAC = 0.42;
/** Half-thickness of the index pad; must match Weapons.__debug.triggerContact. */
const INDEX_HALF = 0.0103;

/** Orthonormal basis with +X along `x` and +Y as close to `yHint` as possible. */
function basis(x, yHint) {
  const X = x.clone().normalize();
  let Y = yHint.clone().addScaledVector(X, -yHint.dot(X));
  if (Y.lengthSq() < 1e-9) Y = new THREE.Vector3(0, 1, 0).addScaledVector(X, -X.y);
  Y.normalize();
  const Z = new THREE.Vector3().crossVectors(X, Y);
  return new THREE.Matrix4().makeBasis(X, Y, Z);
}

function frame(pos, x, yHint) {
  const m = basis(x, yHint);
  m.setPosition(pos);
  return m;
}

const _p = new THREE.Vector3();
function perpOf(p, cyl, out) {
  out.copy(p).sub(cyl.point);
  out.addScaledVector(cyl.dir, -out.dot(cyl.dir));
  return out;
}
function distToAxis(p, cyl) {
  return perpOf(p, cyl, _p).length();
}
function radialAt(p, cyl) {
  const v = perpOf(p, cyl, new THREE.Vector3());
  return v.lengthSq() < 1e-12 ? new THREE.Vector3(0, 1, 0) : v.normalize();
}

/**
 * Curl a chain around a cylinder. Each joint is placed exactly `targetR` from the
 * cylinder axis when the segment can reach it, otherwise as close as the joint limit
 * allows.
 */
function wrapChain(startPos, startDir, curlAxis, lens, cyl, targetR, limits) {
  const joints = [startPos.clone()];
  const dirs = [];
  let dir = startDir.clone().normalize();
  const axis = curlAxis.clone().normalize();

  const probe = (s) => {
    const d = dir.clone().applyAxisAngle(axis, s * 0.30);
    return distToAxis(startPos.clone().addScaledVector(d, lens[0]), cyl);
  };
  const sign = probe(1) <= probe(-1) ? 1 : -1;

  for (let i = 0; i < lens.length; i++) {
    const p = joints[i];
    const base = dir.clone();
    const f = (a) => {
      const d = base.clone().applyAxisAngle(axis, sign * a);
      return distToAxis(p.clone().addScaledVector(d, lens[i]), cyl) - targetR;
    };
    const lim = (limits && limits[i]) ?? 1.55;
    let a;
    if (f(0) <= 0) a = 0.03;
    else if (f(lim) >= 0) a = lim;
    else {
      let lo = 0, hi = lim;
      for (let k = 0; k < 32; k++) { const m = (lo + hi) * 0.5; if (f(m) > 0) lo = m; else hi = m; }
      a = (lo + hi) * 0.5;
    }
    dir = base.applyAxisAngle(axis, sign * a);
    dirs.push(dir.clone());
    joints.push(p.clone().addScaledVector(dir, lens[i]));
  }
  return { joints, dirs, sign };
}

/* ------------------------------------------------------------------ *
 * Continuous swept skin
 * ------------------------------------------------------------------ */

/**
 * Sweep a closed cross-section along a polyline and return a geometry carrying an
 * `aS` attribute — the chain parameter at each vertex — so `WeaponBuilder.add` can
 * derive real skin weights that blend across every joint.
 *
 * @param {THREE.Vector3[]} joints  n+1 points
 * @param {THREE.Vector3[]} dirs    n unit directions
 * @param {number[]} lens           n segment lengths
 * @param {(s:number, a:number)=>[number,number]} sect  cross-section in the ring plane
 * @param {{ringSegs?:number, perSeg?:number, up?:THREE.Vector3, capStart?:boolean,
 *          tip?:number}} opts
 */
function sweep(joints, dirs, lens, sect, opts = {}) {
  const RS = opts.ringSegs ?? 12;
  const PER = opts.perSeg ?? 4;
  const n = dirs.length;
  const tipRings = opts.tip ?? 0;
  const tipLen = opts.tipLen ?? 0;

  // ring schedule: s along the chain, plus `extra` in [0,1] past the final joint
  const sched = [];
  for (let step = 0; step <= n * PER; step++) sched.push({ s: step / PER, extra: 0 });
  for (let k = 1; k <= tipRings; k++) sched.push({ s: n, extra: k / (tipRings + 1) });

  const dirAt = (s) => {
    const j = Math.min(Math.floor(s), n - 1);
    const t = s - j;
    const B = 0.30;
    let d;
    if (t < B && j > 0) d = dirs[j - 1].clone().lerp(dirs[j], 0.5 + 0.5 * (t / B));
    else if (t > 1 - B && j < n - 1) d = dirs[j].clone().lerp(dirs[j + 1], 0.5 * ((t - (1 - B)) / B));
    else d = dirs[j].clone();
    return d.normalize();
  };
  const posAt = (s) => {
    const j = Math.min(Math.floor(s), n - 1);
    const t = Math.min(s - j, 1);
    return joints[j].clone().addScaledVector(dirs[j], t * lens[j]);
  };

  // Parallel-transported frames so the section never flips along the chain.
  let up = (opts.up ?? new THREE.Vector3(0, 1, 0)).clone();
  const pos = [];
  const sArr = [];
  const idx = [];
  const centres = [];
  const R = sched.length;

  for (let r = 0; r < R; r++) {
    const { s, extra } = sched[r];
    const d = dirAt(s);
    up.addScaledVector(d, -up.dot(d));
    if (up.lengthSq() < 1e-9) up.set(0, 1, 0).addScaledVector(d, -d.y);
    up.normalize();
    const right = new THREE.Vector3().crossVectors(d, up);
    const c = posAt(s);
    if (extra > 0) c.addScaledVector(d, extra * tipLen);
    centres.push(c);
    const shrink = extra > 0 ? Math.cos(extra * Math.PI * 0.5) : 1;
    for (let a = 0; a < RS; a++) {
      const ang = (a / RS) * Math.PI * 2;
      const uv = sect(s, ang);
      pos.push(
        c.x + right.x * uv[0] * shrink + up.x * uv[1] * shrink,
        c.y + right.y * uv[0] * shrink + up.y * uv[1] * shrink,
        c.z + right.z * uv[0] * shrink + up.z * uv[1] * shrink,
      );
      sArr.push(s);
    }
  }

  for (let r = 0; r < R - 1; r++) {
    for (let a = 0; a < RS; a++) {
      const b = (a + 1) % RS;
      const i0 = r * RS + a, i1 = r * RS + b, i2 = (r + 1) * RS + b, i3 = (r + 1) * RS + a;
      idx.push(i0, i1, i2, i0, i2, i3);
    }
  }
  // caps
  const capAt = (ringIndex, s, flip) => {
    const base = pos.length / 3;
    const c = centres[ringIndex];
    pos.push(c.x, c.y, c.z);
    sArr.push(s);
    for (let a = 0; a < RS; a++) {
      const src = (ringIndex * RS + a) * 3;
      pos.push(pos[src], pos[src + 1], pos[src + 2]);
      sArr.push(s);
    }
    for (let a = 0; a < RS; a++) {
      const i1 = base + 1 + a, i2 = base + 1 + ((a + 1) % RS);
      if (flip) idx.push(base, i2, i1); else idx.push(base, i1, i2);
    }
  };
  if (opts.capStart !== false) capAt(0, 0, true);
  capAt(R - 1, n, false);

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('aS', new THREE.Float32BufferAttribute(sArr, 1));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

/** Rounded-rectangle (superellipse) cross-section, half-extents w and h. */
function superSect(w, h, p = 3.0) {
  return (ang) => {
    const ca = Math.cos(ang), sa = Math.sin(ang);
    const e = 2 / p;
    return [
      Math.sign(ca) * Math.pow(Math.abs(ca), e) * w,
      Math.sign(sa) * Math.pow(Math.abs(sa), e) * h,
    ];
  };
}

/* ------------------------------------------------------------------ *
 * Trigger finger solve
 * ------------------------------------------------------------------ */

const _AZ = new THREE.Vector3(0, 0, 1);
const _qz = new THREE.Quaternion();

/**
 * Solve the trigger finger by planar inverse kinematics in the BORE plane.
 *
 * Two things had to be true for this to work at all, and neither was before:
 *
 * 1. **The curl plane.** The finger bones' local +Z — the axis the runtime rotates
 *    them about — used to come from the grip cylinder's radial, so the finger swung
 *    sideways across the receiver instead of forward along the bore. Every joint of
 *    this finger is now framed so its local +Z is the weapon's X axis, and the whole
 *    finger articulates in the gun's vertical YZ plane, which is the plane the
 *    trigger actually lives in.
 * 2. **The reach.** A three-link chain of 43 / 25 / 22 mm has to travel forward over
 *    the trigger guard and curl back down onto the blade; the straight-line distance
 *    from the MCP to the blade is only ~19 mm, so this is a large-flexion pose, not
 *    a small one. A single coupled scalar could not express it. The solve searches
 *    the two-parameter family (start angle, coupled curl) and picks the natural
 *    member of it.
 *
 * Angles are measured in the plane from `u` (weapon forward, -Z); positive tilts up.
 *   phi_j = alpha - k * C_j     C = [1, 2, 2.72]
 * so the per-bone runtime deltas between two curls are [dk, dk, 0.72 dk].
 */
function solveTriggerIK(root, target, lens, half, opts = {}) {
  const nrm = new THREE.Vector3(1, 0, 0);          // weapon +X: the curl-plane normal
  const u = new THREE.Vector3(0, 0, -1);           // weapon forward
  const v = new THREE.Vector3(0, 1, 0);            // weapon up
  const _a = new THREE.Vector3(), _b = new THREE.Vector3();

  /*
   * phi0  MCP angle of the proximal phalanx, 0 = straight forward, negative = down
   * f     PIP flexion; the DIP is coupled at 0.72 x, as a human index finger is
   *
   *   phi = [ phi0, phi0 - f, phi0 - 1.72 f ]
   *
   * Reaching a trigger 17 mm from the knuckle with an 89 mm finger is a LARGE
   * flexion pose: the proximal goes forward and down past the blade and the middle
   * phalanx folds back so the distal pad lands on the face of the trigger. That is
   * what a trigger finger does; it is not a small correction to a straight finger.
   */
  const chain = (phi0, f) => {
    const phis = [phi0, phi0 - f, phi0 - 1.72 * f];
    const joints = [root.clone()];
    const dirs = [];
    for (let j = 0; j < 3; j++) {
      const c = Math.cos(phis[j]), sn = Math.sin(phis[j]);
      const d = new THREE.Vector3(
        u.x * c + v.x * sn, u.y * c + v.y * sn, u.z * c + v.z * sn);
      dirs.push(d);
      joints.push(joints[j].clone().addScaledVector(d, lens[j]));
    }
    return { joints, dirs, phis };
  };

  const dist = (c) => {
    const j2 = c.joints[2], ax = c.dirs[2];
    const t = THREE.MathUtils.clamp(_a.copy(target).sub(j2).dot(ax), 0, lens[2]);
    return _b.copy(j2).addScaledVector(ax, t).distanceTo(target);
  };

  const cost = (phi0, f) => {
    if (f < 0.15 || f > 2.35) return 1e6;
    if (phi0 < -1.15 || phi0 > 0.45) return 1e6;
    const c = chain(phi0, f);
    let e = Math.abs(dist(c) - half) * 1400;                 // mm, dominant term
    e += Math.abs(f - 1.55) * 1.1;                           // human PIP flexion
    e += Math.abs(phi0 + 0.35) * 1.1;                        // proximal ~20 deg down
    // the middle joint must stay forward of the knuckle and below the receiver
    const j1 = c.joints[1];
    if (j1.z > root.z - 0.004) e += (j1.z - root.z + 0.004) * 2200;
    if (j1.y > root.y) e += (j1.y - root.y) * 2200;
    return e;
  };

  let bestP = -0.35, bestF = 1.55, bestC = Infinity;
  for (let pi = 0; pi <= 32; pi++) {
    const phi0 = -1.15 + pi * 0.05;
    for (let fi = 0; fi <= 44; fi++) {
      const f = 0.15 + fi * 0.05;
      const c = cost(phi0, f);
      if (c < bestC) { bestC = c; bestP = phi0; bestF = f; }
    }
  }
  let sp = 0.025, sf = 0.025;
  for (let it = 0; it < 90; it++) {
    let improved = false;
    for (const [dp, df] of [[sp, 0], [-sp, 0], [0, sf], [0, -sf],
      [sp, sf], [-sp, -sf], [sp, -sf], [-sp, sf]]) {
      const c = cost(bestP + dp, bestF + df);
      if (c < bestC - 1e-9) { bestC = c; bestP += dp; bestF += df; improved = true; }
    }
    if (!improved) { sp *= 0.6; sf *= 0.6; }
    if (sp < 5e-5) break;
  }

  // Rest = the same finger with the trigger not yet broken: a little less flexion
  // and the knuckle a little higher, so the pad sits just forward of the blade.
  const dPhi0 = opts.restLift ?? 0.13;
  const restF = bestF * (opts.restFlex ?? 0.62);
  const dK = bestF - restF;
  return {
    nrm,
    rest: chain(bestP + dPhi0, restF),
    pressed: chain(bestP, bestF),
    // per-bone runtime deltas that take the bind (rest) pose to the pressed pose
    curl: [dPhi0, dK, dK * 0.72],
    phi0: bestP, flex: bestF,
    residualMm: +((dist(chain(bestP, bestF)) - half) * 1000).toFixed(3),
  };
}

const M = (a, b) => new THREE.Matrix4().multiplyMatrices(a, b);

/**
 * @param {import('./Builder.js').WeaponBuilder} B
 * @param {*} rng
 * @param {{side:'left'|'right', cyl:{point:THREE.Vector3,dir:THREE.Vector3,radius:number},
 *          heelRadial:number[], along?:number, gap?:number, forearmDir:number[],
 *          parent?:string, trigger?:boolean, triggerTarget?:THREE.Vector3,
 *          scale?:number, palmLimits?:number[],
 *          thumbAlong?:number, thumbLift?:number, spread?:number}} cfg
 */
export function buildHand(B, rng, cfg) {
  const s = cfg.side;
  const S = cfg.scale ?? 0.95;
  const sgn = s === 'right' ? 1 : -1;
  const parent = cfg.parent ?? 'root';

  const cyl = {
    point: cfg.cyl.point.clone(),
    dir: cfg.cyl.dir.clone().normalize(),
    radius: cfg.cyl.radius,
  };
  const palmLen = PALM_LEN * S, palmW = PALM_W * S, palmT = PALM_T * S;
  const gap = cfg.gap ?? 0.0012;
  const palmR = cyl.radius + palmT * 0.5 + gap;

  /* ---------------- wrap the palm around the grip ---------------- */
  const radial0 = new THREE.Vector3(...cfg.heelRadial).normalize();
  radial0.addScaledVector(cyl.dir, -radial0.dot(cyl.dir)).normalize();
  const heel = cyl.point.clone()
    .addScaledVector(cyl.dir, cfg.along ?? 0)
    .addScaledVector(radial0, palmR);
  const tangent0 = new THREE.Vector3().crossVectors(radial0, cyl.dir).normalize();

  const palm = wrapChain(heel, tangent0, cyl.dir, PALM_SEG.map((f) => f * palmLen),
    cyl, palmR, cfg.palmLimits ?? [0.62, 0.58, 0.50]);

  const palmFrames = palm.joints.slice(0, 3).map((p, i) =>
    frame(p, palm.dirs[i], radialAt(p, cyl)));
  const handWorld = palmFrames[0];
  const mcp = palm.joints[3];
  const mcpFrame = frame(mcp, palm.dirs[2], radialAt(mcp, cyl));

  const hx = new THREE.Vector3(), hy = new THREE.Vector3(), hz = new THREE.Vector3();
  mcpFrame.extractBasis(hx, hy, hz);

  const armWorld = frame(
    heel.clone().addScaledVector(palm.dirs[0], -0.012).addScaledVector(radial0, 0.004),
    new THREE.Vector3(...cfg.forearmDir).normalize(), radial0);

  B.boneAt(`${s}_arm`, parent, armWorld);
  B.boneAt(`${s}_hand`, `${s}_arm`, handWorld);
  const HW = B.worldOf(`${s}_hand`).clone();

  /* ---------------- forearm sleeve + glove cuff ---------------- */
  {
    const AW = B.worldOf(`${s}_arm`).clone();
    const len = 0.245 * S;
    const rW = 0.0322 * S, rE = 0.0462 * S;
    B.add(chamferCone(rW, rE, len, 0.002, 18).rotateZ(-Math.PI / 2).translate(len * 0.5, 0, 0),
      'sleeve', `${s}_arm`, AW, { uvScale: 22, occlude: false });
    B.add(chamferCyl(rE, 0.006, 0.001, 18).rotateZ(-Math.PI / 2).translate(len - 0.003, 0, 0),
      'sleeve', `${s}_arm`, AW, { uvScale: 22, occlude: false });
    /*
     * The gauntlet and cuff are ~12 % slimmer than they were. A 0.0386 cuff is
     * a 73 mm-diameter wrist — fatter than the 55 mm a glove cuff actually is,
     * and every millimetre of that radius put the cuff's top edge higher over
     * the receiver (see the note on `forearmDir` in `WeaponDefs`).
     */
    B.add(chamferCone(0.0310 * S, 0.0338 * S, 0.062 * S, 0.0012, 18)
      .rotateZ(-Math.PI / 2).translate(0.028 * S, 0, 0), 'glove', `${s}_arm`, AW, { occlude: false });
    // cuff strap with a hook-and-loop tab, so the wrist actually terminates
    B.add(chamferCyl(0.0352 * S, 0.0128 * S, 0.0012, 18)
      .rotateZ(-Math.PI / 2).translate(0.040 * S, 0, 0), 'webbing', `${s}_arm`, AW, {});
    B.add(bevelBox(0.0210, 0.0120, 0.0042, 0.0009), 'webbing', `${s}_arm`,
      M(AW, place(0.040 * S, 0.0338 * S, 0.004)), {});
    B.add(bevelBox(0.0074, 0.0060, 0.0030, 0.0008), 'rubber', `${s}_arm`,
      M(AW, place(0.048 * S, 0.0350 * S, 0.004)), {});
  }

  /* ---------------- palm: one continuous loft ---------------- */
  const thenarSide = -sgn;   // thumb is on -Z for a right hand
  {
    const wAt = [0.86, 1.00, 1.00];
    const tAt = [0.98, 0.95, 0.86];
    /*
     * WEB TRIM — the last of the "dark sliver crossing the receiver" family.
     *
     * The palm loft's WIDTH runs along the grip axis (the sweep's section X is
     * +/-cyl.dir), so an 82 mm palm reaches 37 mm either side of the heel. The
     * heel sits only ~20 mm below the lower receiver, so the web side of the
     * palm stood ~19 mm PROUD of the receiver's underside — measured by
     * raycast at `wpn_dbg_grip` (890,268) and (940,300): `wpn_glove` on
     * `right_hand` at weapon y = -0.0067 and -0.0120 against a receiver
     * underside of -0.0178. Seen edge-on that overhang is a thin unlit arc
     * across the receiver, which is what the reviewer keeps finding.
     *
     * Anatomically there is no palm above the web of the hand — that is where
     * the thumb starts — so the fix is to terminate the loft there rather than
     * to move the hand. Moving the hand was tried: 16 mm down the grip took
     * the trigger-pressed contact from 0.00 mm to 2.85 mm, because the trigger
     * IK is solved from the MCP. This trims only the section, so the MCP, the
     * wrap and every solved contact are untouched.
     */
    const webTrim = cfg.webTrim ?? 1;
    const _rd = new THREE.Vector3().crossVectors(palm.dirs[0], radialAt(palm.joints[0], cyl));
    const webPositive = _rd.dot(cyl.dir) < 0;   // does section +X run toward the receiver?
    const at = (arr, s01) => {
      const x = THREE.MathUtils.clamp(s01, 0, 2.999);
      const i = Math.floor(x), f = x - i;
      return arr[i] + (arr[Math.min(i + 1, 2)] - arr[i]) * f;
    };
    const sect = (sp, ang) => {
      const w = palmW * 0.5 * at(wAt, sp);
      const h = palmT * 0.5 * at(tAt, sp);
      const base = superSect(w, h, 3.4)(ang);
      // thenar eminence: a lobe on the thumb side, strongest across the middle
      const lobe = Math.exp(-Math.pow((sp - 1.25) / 0.75, 2))
        * Math.max(0, Math.cos(ang - (thenarSide > 0 ? 0 : Math.PI)))
        * palmW * 0.16;
      // hypothenar pad on the little-finger side, softer
      const hypo = Math.exp(-Math.pow((sp - 1.5) / 0.85, 2))
        * Math.max(0, Math.cos(ang - (thenarSide > 0 ? Math.PI : 0)))
        * palmW * 0.07;
      const k = 1 + (lobe + hypo) / Math.max(1e-4, Math.hypot(base[0], base[1]));
      const x = base[0] * k;
      return [(webTrim !== 1 && (x > 0) === webPositive) ? x * webTrim : x, base[1] * k];
    };
    const lens = PALM_SEG.map((f) => f * palmLen);
    const g = sweep(palm.joints, palm.dirs, lens, sect, {
      ringSegs: 16, perSeg: 4, up: radialAt(palm.joints[0], cyl), tip: 0, tipLen: 0,
    });
    B.add(g, 'glove', `${s}_hand`, null, { edgeWear: 0.05, occlude: false });

    /*
     * Knuckle rise over the MCP row.
     *
     * "A four-cylinder mitten" — these are the four cylinders. They stood
     * 6.2 mm proud of the back of the hand on 8.6 mm discs, which from any
     * angle that does not show the fingers reads as four studs glued to a
     * slab. Hard-knuckle armour on a real glove is a LOW moulded shell that
     * follows the knuckle it covers; half-buried in the loft it reads as
     * knuckles instead of as hardware.
     */
    for (let i = 0; i < 4; i++) {
      const lat = (i - 1.5) * (cfg.spread ?? 0.0205 * S) * sgn;
      const rr = i === 3 ? 0.0070 : 0.0080;
      B.add(chamferCyl(rr, 0.0030, 0.0014, 12), 'rubber', `${s}_hand`,
        M(mcpFrame, place(-0.0052, palmT * 0.400, lat)), {});
    }
    // stitched seam down the back of the hand
    B.add(bevelBox(palmLen * 0.74, 0.0022, 0.0026, 0.0006), 'webbing', `${s}_hand`,
      M(palmFrames[1], place(PALM_SEG[1] * palmLen * 0.5, palmT * 0.50, 0)), {});
    /*
     * Wrist seam where the gauntlet meets the palm.
     *
     * This was 3.4 mm thick and `palmW * 0.86` = 70 mm long, and its length
     * runs along the GRIP AXIS — so it reached 32 mm up the grip from the palm
     * heel, to weapon y = -0.0057, i.e. 12 mm above the top edge of the lower
     * receiver. A 3.4 mm plate seen edge-on above the receiver is exactly the
     * "small dark sliver crossing the receiver" the reviewer reports in
     * `wpn_hands` and `wpn_dbg_grip`; raycasting (870,255) in `wpn_dbg_grip`
     * lands on `wpn_webbing` / `right_hand` at (0.0313, -0.0052, 0.0372),
     * which is this part. At 0.54 it terminates below the receiver line, and
     * a hair thicker so it reads as a stitched seam rather than as a blade.
     */
    B.add(bevelBox(0.0042, palmT * 0.96, palmW * 0.54, 0.0008), 'webbing', `${s}_hand`,
      M(palmFrames[0], place(0.0016, 0, 0)), {});

    /*
     * Suede palm reinforcement with real stipple, on the grip side.
     *
     * These were 2.6 mm thick, 74 % of the palm width and seated at
     * -0.47 palmT — i.e. a FLAT box whose outer face sat 1.3-3.0 mm PROUD of a
     * CURVED palm, so its corners broke out through the glove and the stipple
     * field behind it broke out further still. Rendered as a material ID that
     * is what the reviewer's "stray thin rods" at `wpn_dbg_grip` (1000,520) and
     * (930,470) actually are: webbing geometry outside the glove surface.
     * Thinner, narrower and seated INSIDE the loft, so the pad reads as a patch
     * sewn onto the palm rather than as a slab floating beside it.
     */
    for (let i = 0; i < 3; i++) {
      const L = PALM_SEG[i] * palmLen;
      const F = palmFrames[i];
      B.add(bevelBox(L * 0.84, 0.0016, palmW * 0.58, 0.0007, { corner: 0.0060 }),
        'webbing', `${s}_hand`, M(F, place(L * 0.5, -palmT * 0.435, 0)), {});
      const grippy = stippleField(L * 0.72, palmW * 0.50, rng,
        { pitch: 0.0036, height: 0.00030, jitter: 0.35 });
      grippy.rotateX(Math.PI / 2);
      B.add(grippy, 'webbing', `${s}_hand`,
        M(F, place(L * 0.5, -palmT * 0.443 - 0.0006, 0)), { noAO: true });
    }
  }

  /* ---------------- fingers ---------------- */
  const spread = cfg.spread ?? 0.0205 * S;
  const fingerBones = [];
  let triggerCurl = null;

  for (let i = 0; i < FINGERS.length; i++) {
    const F = FINGERS[i];
    const lens = F.lens.map((l) => l * S);
    const lateral = (i - 1.5) * spread * sgn;
    const setback = [0.0, -0.0016, -0.0042, -0.0086][i] * S;
    const root = mcp.clone()
      .addScaledVector(hz, lateral)
      .addScaledVector(hx, setback)
      .addScaledVector(hy, -palmT * 0.06);

    const isTrigger = cfg.trigger && i === 0 && !!cfg.triggerTarget;
    let chain, ik = null;
    if (isTrigger) {
      ik = solveTriggerIK(root, cfg.triggerTarget, lens, INDEX_HALF);
      chain = ik.rest;
      triggerCurl = ik.curl;
    } else if (cfg.trigger && i === 0) {
      chain = straightChain(root, hx, hz, lens, [-0.18, -0.14, -0.10]);
    } else {
      const halfT = F.h * S * 0.5 + 0.0012;
      chain = wrapChain(root, hx, hz, lens, cyl, cyl.radius + halfT, [1.45, 1.55, 1.40]);
    }

    const names = [];
    for (let j = 0; j < 3; j++) {
      const name = `${s}_${F.name}${j}`;
      // For the trigger finger the local +Z must be the curl-plane normal, so the
      // runtime's rotation about local Z articulates it in the bore plane.
      const yHint = ik
        ? new THREE.Vector3().crossVectors(ik.nrm, chain.dirs[j])
        : radialAt(chain.joints[j], cyl);
      B.boneAt(name, j === 0 ? `${s}_hand` : `${s}_${F.name}${j - 1}`,
        frame(chain.joints[j], chain.dirs[j], yHint));
      names.push(name);
    }
    fingerBones.push(names);

    // one continuous surface across all three phalanges
    const w0 = F.w * S * 0.5, h0 = F.h * S * 0.5;
    const sect = (sp, ang) => {
      const t = sp / 3;
      const taper = 1 - 0.20 * t;
      // knuckles swell where the joints are
      const knu = 1 + 0.11 * Math.exp(-Math.pow((sp - 1) / 0.22, 2))
        + 0.09 * Math.exp(-Math.pow((sp - 2) / 0.20, 2));
      const w = w0 * taper * knu;
      const h = h0 * taper * knu;
      const p = superSect(w, h, 3.2)(ang);
      // the pad side (-Y) is flatter than the nail side
      if (p[1] < 0) p[1] *= 0.90;
      return p;
    };
    const g = sweep(chain.joints, chain.dirs, lens, sect, {
      ringSegs: 12, perSeg: 4, up: radialAt(chain.joints[0], cyl),
      tip: 3, tipLen: h0 * 1.05, capStart: true,
    });
    B.add(g, 'glove', names[0], null, { chain: names, edgeWear: 0.03, occlude: false });

    // Lateral stitching welts, the tell that a glove is a glove.
    // Seated at 0.93 of the finger half-width, not 1.02: at 1.02 the welt's
    // own section sat entirely OUTSIDE the digit and rendered as a detached
    // thin rod whenever the finger behind it was occluded.
    for (const sside of [-1, 1]) {
      const welt = (sp, ang) => {
        const t = sp / 3;
        const w = w0 * (1 - 0.20 * t) * 0.93;
        const h = h0 * (1 - 0.20 * t) * 0.30;
        const p = superSect(w * 0.10, h, 2.4)(ang);
        return [p[0] + sside * w, p[1] * 0.35];
      };
      const gw = sweep(chain.joints, chain.dirs, lens, welt, {
        ringSegs: 6, perSeg: 2, up: radialAt(chain.joints[0], cyl),
        tip: 0, tipLen: 0, capStart: false,
      });
      B.add(gw, 'webbing', names[0], null, { chain: names, noAO: true });
    }
    // knuckle armour on the proximal phalanx, as on a real hard-knuckle glove
    const W0 = B.worldOf(names[0]).clone();
    B.add(bevelBox(lens[0] * 0.46, 0.0024, F.w * S * 0.68, 0.0008, { corner: 0.0022 }),
      'rubber', names[0], M(W0, place(lens[0] * 0.46, h0 * 1.02, 0)), {});
  }

  /* ---------------- thumb ----------------
   *
   * Two defects the critique named, both from this block:
   *
   * 1. "Black spikes" over the receiver in `wpn_closeup` and `wpn_hands`.
   *    Rendered as a material ID they are `glove` — the RIGHT thumb. `bz` is
   *    the grip cylinder's own axis, so `-sgn * palmW * 0.42` displaced the
   *    thumb's root **34 mm UP the pistol grip** from a palm heel that is
   *    already only 22 mm below the receiver. The thumb therefore started
   *    12 mm ABOVE the top of the grip and swept 94 mm of digit out through
   *    the top of the gun. `thumbAxialFrac` is that displacement, and the
   *    right hand now asks for a third of it.
   *
   * 2. "A four-cylinder mitten with no thumb" on the support hand. The left
   *    thumb was displaced the same 34 mm but REARWARD along the handguard,
   *    where the palm mass hides it completely. `thumbAxial` lays it along
   *    the tube instead — the modern C-clamp — and roots it ON the cylinder
   *    surface so `wrapChain` still lands every joint at exactly `targetR`
   *    and the measured grip contact is unchanged.
   */
  {
    const lens = THUMB.lens.map((l) => l * S);
    const base = palmFrames[1];
    const bx = new THREE.Vector3(), by = new THREE.Vector3(), bz = new THREE.Vector3();
    base.extractBasis(bx, by, bz);
    const thumbR = cyl.radius + THUMB.h * S * 0.5 + 0.0014;
    const axial = cfg.thumbAxial ?? 0;
    let root, startDir;
    if (axial > 0) {
      // C-clamp: rooted on the tube, running along it toward the muzzle.
      const along0 = _p.copy(palm.joints[1]).sub(cyl.point).dot(cyl.dir);
      const rad = radialAt(palm.joints[1], cyl)
        .applyAxisAngle(cyl.dir, sgn * (cfg.thumbAzimuth ?? 0));
      root = cyl.point.clone()
        .addScaledVector(cyl.dir, along0 + (cfg.thumbAxialFrac ?? -0.36) * palmW)
        .addScaledVector(rad, thumbR);
      const tang = new THREE.Vector3().crossVectors(rad, cyl.dir);
      startDir = cyl.dir.clone().multiplyScalar(-axial)
        .addScaledVector(tang, 1 - axial).normalize();
    } else {
      root = palm.joints[1].clone()
        .addScaledVector(bx, PALM_SEG[1] * palmLen * (cfg.thumbAlong ?? 0.62))
        .addScaledVector(bz, (cfg.thumbAxialFrac ?? -sgn * 0.42) * palmW)
        .addScaledVector(by, -palmT * 0.04);
      startDir = bx.clone()
        .applyAxisAngle(by, sgn * (cfg.thumbYaw ?? 0.62))
        .applyAxisAngle(bz, cfg.thumbLift ?? 0.20)
        .normalize();
    }

    /*
     * `thumbLimits` is a per-hand override and is deliberately NOT used to fix
     * a thumb that ends up in the wrong place. `wrapChain` bisects for the
     * angle that lands each joint exactly on the grip surface; capping below
     * that angle leaves the thumb floating (measured: worst grip contact
     * 0.04 mm -> 6.09 mm). Aim the thumb with `thumbYaw` / `thumbAxialFrac`
     * instead — see the note in `WeaponDefs.defaultHandPoses`.
     */
    const chain = cfg.thumbWrap === false
      ? straightChain(root, startDir, bz, lens, [0.28, 0.22, 0.16])
      : wrapChain(root, startDir, bz, lens, cyl, thumbR,
        cfg.thumbLimits ?? [1.35, 1.30, 1.10]);

    const names = [];
    for (let j = 0; j < 3; j++) {
      const name = `${s}_thumb${j}`;
      B.boneAt(name, j === 0 ? `${s}_hand` : `${s}_thumb${j - 1}`,
        frame(chain.joints[j], chain.dirs[j], radialAt(chain.joints[j], cyl)));
      names.push(name);
    }
    fingerBones.push(names);

    const w0 = THUMB.w * S * 0.5, h0 = THUMB.h * S * 0.5;
    const sect = (sp, ang) => {
      const t = sp / 3;
      const taper = 1 - 0.26 * t;
      const knu = 1 + 0.10 * Math.exp(-Math.pow((sp - 1) / 0.24, 2))
        + 0.08 * Math.exp(-Math.pow((sp - 2) / 0.22, 2));
      const p = superSect(w0 * taper * knu, h0 * taper * knu, 3.2)(ang);
      if (p[1] < 0) p[1] *= 0.90;
      return p;
    };
    const g = sweep(chain.joints, chain.dirs, lens, sect, {
      ringSegs: 12, perSeg: 4, up: radialAt(chain.joints[0], cyl),
      tip: 3, tipLen: h0 * 1.05,
    });
    B.add(g, 'glove', names[0], null, { chain: names, edgeWear: 0.03, occlude: false });
    for (const sside of [-1, 1]) {
      const welt = (sp, ang) => {
        const t = sp / 3;
        const w = w0 * (1 - 0.26 * t) * 0.93;
        const h = h0 * (1 - 0.26 * t) * 0.30;
        const p = superSect(w * 0.10, h, 2.4)(ang);
        return [p[0] + sside * w, p[1] * 0.35];
      };
      B.add(sweep(chain.joints, chain.dirs, lens, welt, {
        ringSegs: 6, perSeg: 2, up: radialAt(chain.joints[0], cyl),
        tip: 0, tipLen: 0, capStart: false,
      }), 'webbing', names[0], null, { chain: names, noAO: true });
    }
  }

  return {
    fingerBones, handBone: `${s}_hand`, armBone: `${s}_arm`, mcp, palm,
    triggerCurl, indexDistal: FINGERS[0].lens[2] * S,
  };
}

function straightChain(root, startDir, axis, lens, bends) {
  const joints = [root.clone()];
  const dirs = [];
  let d = startDir.clone().normalize();
  for (let j = 0; j < lens.length; j++) {
    d = d.clone().applyAxisAngle(axis, bends[j] ?? 0.2);
    dirs.push(d.clone());
    joints.push(joints[j].clone().addScaledVector(d, lens[j]));
  }
  return { joints, dirs };
}

/** Place both hands on a weapon from its anchor description. */
export function buildHandsFor(B, rng, spec) {
  const out = {};
  for (const side of ['right', 'left']) {
    const a = spec[side];
    if (!a) continue;
    out[side] = buildHand(B, rng, { side, ...a });
  }
  return out;
}
